import React, {useMemo} from "react";
import classnames from "classnames";
import Barcode from "react-barcode";
import {useTranslation} from "react-i18next";
import ElementIds from "../../../../../core/models/element-ids";
import Utils, {createUUId, formatMoney, formatPhoneNumber} from "../../../../../core/services/utils/utils";
import DateUtils from "../../../../../core/services/date-utils";

/**
 * @param {string|object} serial
 * @returns {string}
 */
const serialValue = (serial) => {
    if (!serial) {
        return '';
    }
    if (typeof serial === 'string') {
        return serial;
    }
    return serial.serialNumber ?? serial.value ?? '';
};

/**
 * Creates a product line row for the purchase print template.
 */
const createProductRow = (item, index, length) => {
    const hasSubRows = (item?.serials?.length ?? 0) > 0;
    const qty = Number(item?.orderQty) || 0;
    const unitPrice = Number(item?.cost) || 0;
    const disc = Number(item?.discountPct) || 0;
    const extended = item?.extendedCost != null
        ? Number(item.extendedCost)
        : qty * unitPrice * (1 - disc / 100);

return (
        <tr
            key={item.id ?? item.partNo ?? index}
            className={classnames('table-body side-borders', {
                'no-bottom-border': hasSubRows && index !== length - 1,
            })}
        >
            <td colSpan={10} className={'text-left font-weight-semi-bold text-s part-number-fluid no-right-border'}>
                {item?.vendorPartNo ?? ''}
            </td>
            <td colSpan={16} className={'text-left font-weight-semi-bold text-s no-left-border no-right-border'}>
                {item?.description ?? ''}
            </td>
            <td colSpan={10} className={'text-left font-weight-semi-bold text-s part-number-fluid no-left-border border-right'}>
                {item?.partNo ?? ''}
            </td>
            <td colSpan={4} className={'text-center font-weight-semi-bold text-s no-left-border no-right-border'}>
                {qty || ''}
            </td>
            <td colSpan={3} className={'text-center font-weight-semi-bold text-s no-left-border no-right-border'}>
                {item?.unit ?? 'EA'}
            </td>
            <td colSpan={4} className={'text-center font-weight-semi-bold text-s no-left-border border-right'}>
                {disc ? `${disc}%` : ''}
            </td>
            <td colSpan={6} className={'text-right font-weight-semi-bold text-s no-left-border no-right-border'}>
                {formatMoney(unitPrice)?.replace('$', '') ?? ''}
            </td>
            <td colSpan={7} className={'text-right font-weight-semi-bold text-s no-left-border'}>
                {formatMoney(extended)?.replace('$', '') ?? ''}
            </td>
        </tr>
    );
};

/**
 * Creates serial sub-rows (two serials per row).
 * Keeps both vertical column rules (after Part No, after Disc %).
 */
const createSerialNoRow = ({first = '', second = '', key}) => (
    <tr key={key} className={'p-0 no-top-border no-bottom-border serial-number side-borders'}>
        <td colSpan={10} className={'pl-3 text-left font-weight-semi-bold font-italic text-xs no-right-border'}>
            {first ? `S/N: ${first}` : ''}
        </td>
        <td colSpan={16} className={'pl-3 text-left font-weight-semi-bold font-italic text-xs no-left-border no-right-border'}>
            {second ? `S/N: ${second}` : ''}
        </td>
        <td colSpan={10} className={'no-left-border border-right'}/>
        <td colSpan={11} className={'no-left-border border-right'}/>
        <td colSpan={13} className={'no-left-border'}/>
    </tr>
);

/**
 * Formats an address block for To / Ship To.
 */
const formatAddressLines = (address) => {
    if (!address) {
        return [];
    }
    const lines = [];
    if (address.fullAddress) {
        lines.push(address.fullAddress);
    }
    const cityLine = [address.city, address.province, address.postalCode].filter(Boolean).join(' ');
    if (cityLine) {
        lines.push(cityLine);
    }
    if (address.country) {
        lines.push(address.country);
    }
    if (address.phone) {
        lines.push(formatPhoneNumber(address.phone) || address.phone);
    }
    return lines;
};

/**
 * Hidden full-document template used to measure purchase print pages.
 */
const PrintPurchaseTemplate = ({document: purchaseDocument, partner, history = false}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.print.purchase'});

    const documentNo = purchaseDocument?.documentNo
        ?? purchaseDocument?.orderNo
        ?? purchaseDocument?.historyNo
        ?? '';

    const items = useMemo(() => (purchaseDocument?.items ?? []).map((item) => {
        const pairs = [];
        (item?.serials ?? []).forEach((serial) => {
            const value = serialValue(serial);
            if (!value) {
                return;
            }
            if (!pairs.length || pairs[pairs.length - 1].length === 2) {
                pairs.push([value]);
            } else {
                pairs[pairs.length - 1].push(value);
            }
        });
        const serialRows = pairs.map(([first, second]) => createSerialNoRow({
            first,
            second: second ?? '',
            key: createUUId(true),
        }));
        return {...item, serialRows};
    }), [purchaseDocument?.items]);

    const billingLines = useMemo(
        () => formatAddressLines(purchaseDocument?.billingAddress),
        [purchaseDocument?.billingAddress],
    );
    const shippingLines = useMemo(
        () => formatAddressLines(purchaseDocument?.shippingAddress),
        [purchaseDocument?.shippingAddress],
    );

    const secondaryDate = history
        ? purchaseDocument?.receivedDate
        : purchaseDocument?.requiredDate;

    const netAmount = purchaseDocument?.subtotal ?? 0;
    const freight = purchaseDocument?.freight ?? 0;
    const discount = purchaseDocument?.discount ?? 0;
    const tax = purchaseDocument?.tax ?? 0;
    const totalDue = purchaseDocument?.total ?? purchaseDocument?.balance ?? 0;

    /**
     * Builds product body rows including serial sub-rows.
     */
    const createProductTableBody = () => items.flatMap((item, index) => {
        const rows = [createProductRow(item, index, items.length)];
        return rows.concat(item.serialRows ?? []);
    });

    return (
        <div id={ElementIds.printPurchaseView.page} className={'print-invoice-template'}>
            <table id={ElementIds.printPurchaseView.table} width={'100%'} className={'table'}>
                <colgroup>
                    {
                        Array(60).fill(1).map((_, index) => (
                            <col key={index} width="1.6666666666667%"/>
                        ))
                    }
                </colgroup>
                <tbody id={ElementIds.printPurchaseView.pageHeader}>
                <tr>
                    <td colSpan={20} className={'no-border pr-4'}>
                        <img
                            src={Utils.encodeFileUrl(partner.logo)}
                            style={{objectFit: 'contain', height: 'fit-content', maxHeight: 100}}
                            className={'w-100'}
                            alt={t('logoAlt')}
                        />
                    </td>
                    <td colSpan={20} className={'no-border'}>
                        <p className={'text-md font-weight-bold black line-height-2'}>
                            {partner.fullAddress}
                        </p>
                        <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                            {partner.city} {partner.province} {partner.postalCode}
                        </p>
                        <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                            {t('phone')} <span>{partner.phone}</span>
                            {partner.fax ? ` ${t('fax')} ${partner.fax}` : ''}
                        </p>
                        {
                            partner.email &&
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                {partner.email}
                            </p>
                        }
                        <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                            {Utils.formatLinkUrlDisplay(partner.website) ?? ''}
                        </p>
                    </td>
                    <td colSpan={20} className={'no-border text-right'}>
                        <p className={'invoice-text black font-weight-bold mb-2'}>
                            {history ? t('titleHistory') : t('titleOrder')}
                        </p>
                        <p className={'text-md'}>
                            <span className={'font-weight-bold'} style={{paddingRight: 40}}>
                                {t('date')}
                            </span>
                            <span className={'font-weight-semi-bold text-sm'}>
                                {
                                    purchaseDocument?.date
                                        ? DateUtils.serverToLocal(purchaseDocument.date).format(DateUtils.dateFormatAlternative)
                                        : ''
                                }
                            </span>
                        </p>
                        {
                            !!documentNo &&
                            <div className={'d-flex justify-content-end mt-2'}>
                                <Barcode
                                    value={String(documentNo)}
                                    format={'CODE39'}
                                    width={1.2}
                                    height={40}
                                    displayValue={false}
                                    margin={0}
                                />
                            </div>
                        }
                        <p className={'font-weight-bold text-lg black mt-1'}>
                            {documentNo}
                        </p>
                    </td>
                </tr>
                <tr>
                    <td colSpan={45} className={'no-border'} height={'24px'}/>
                    <td colSpan={15} className={'no-border'} height={'24px'}>
                        <p className={'page-identifier'}>
                            {t('pagePlaceholder')}
                        </p>
                    </td>
                </tr>
                <tr>
                    <td colSpan={30} className={'no-border pt-2'}>
                        <p className={'text-md font-weight-bold mr-2'}>
                            <span style={{paddingRight: 24}}>{t('to')}</span>
                            <span>{purchaseDocument?.vendorName ?? ''}</span>
                        </p>
                        {
                            billingLines.map((line) => (
                                <p key={line} className={'text-md font-weight-semi-bold'}>
                                    <span style={{paddingLeft: 56}}>{line}</span>
                                </p>
                            ))
                        }
                        {
                            purchaseDocument?.vendorNo &&
                            <p className={'text-md font-weight-semi-bold pt-1'}>
                                <span style={{paddingLeft: 56}}>{purchaseDocument.vendorNo}</span>
                            </p>
                        }
                    </td>
                    <td colSpan={30} className={'no-border pt-2'}>
                        <p className={'text-md font-weight-bold mr-2'}>
                            <span style={{paddingRight: 24}}>{t('shipTo')}</span>
                            <span>{purchaseDocument?.shippingAddress?.name ?? ''}</span>
                        </p>
                        {
                            shippingLines.map((line) => (
                                <p key={line} className={'text-md font-weight-semi-bold'}>
                                    <span style={{paddingLeft: 72}}>{line}</span>
                                </p>
                            ))
                        }
                    </td>
                </tr>
                <tr>
                    <td className={'no-border'} colSpan={60} height={'24px'}/>
                </tr>
                </tbody>

                <tbody id={ElementIds.printPurchaseView.productsTable.header}>
                <tr className={'side-top-borders'}>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.reference')}</th>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.buyer')}</th>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.orderNumber')}</th>
                    <th colSpan={15} className={'text-center text-md'}>
                        {history ? t('meta.receivedDate') : t('meta.requiredDate')}
                    </th>
                </tr>
                <tr className={'side-borders'}>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.reference || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.buyer || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {documentNo || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {
                            secondaryDate
                                ? DateUtils.serverToLocal(secondaryDate).format(DateUtils.dateFormatAlternative)
                                : '--'
                        }
                    </td>
                </tr>
                <tr className={'side-borders'}>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.shipVia')}</th>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.paymentTerms')}</th>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.status')}</th>
                    <th colSpan={15} className={'text-center text-md'}>{t('meta.incoterms')}</th>
                </tr>
                <tr className={'side-borders'}>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.shipVia || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.paymentTerms || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.status || '--'}
                    </td>
                    <td colSpan={15} className={'text-center text-sm'}>
                        {purchaseDocument?.incoterms || '--'}
                    </td>
                </tr>
                <tr className={'side-borders'}>
                    <th colSpan={36} className={'border-right'}/>
                    <th colSpan={11} className={'no-left-border border-right'}/>
                    <th colSpan={13} className={'no-left-border'}/>
                </tr>
                <tr className={'side-borders'}>
                    <th colSpan={10} className={'text-left text-md align-bottom no-right-border'}>
                        {t('columns.vendorPartNo')}
                    </th>
                    <th colSpan={16} className={'text-left text-md align-bottom no-left-border no-right-border'}>
                        {t('columns.description')}
                    </th>
                    <th colSpan={10} className={'text-left text-md align-bottom no-left-border border-right'}>
                        {t('columns.partNo')}
                    </th>
                    <th colSpan={4} className={'text-center text-md align-bottom no-left-border no-right-border'}>
                        {t('columns.ordered')}
                    </th>
                    <th colSpan={3} className={'text-center text-md align-bottom no-left-border no-right-border'}>
                        {t('columns.unit')}
                    </th>
                    <th colSpan={4} className={'text-center text-md align-bottom no-left-border border-right'}>
                        {t('columns.discPct')}
                    </th>
                    <th colSpan={6} className={'text-right text-md align-bottom no-left-border no-right-border'}>
                        {t('columns.unitPrice')}
                    </th>
                    <th colSpan={7} className={'text-right text-md align-bottom no-left-border'}>
                        {t('columns.extendedPrice')}
                    </th>
                </tr>
                </tbody>

                <tbody id={ElementIds.printPurchaseView.productsTable.body} className={'table-border'}>
                {createProductTableBody()}
                <tr
                    id={ElementIds.printPurchaseView.productsTable.bodyFiller}
                    className={classnames('no-bottom-border no-top-border side-borders')}
                >
                    <td colSpan={36} className={'p-0 border-right'}/>
                    <td colSpan={11} className={'p-0 no-left-border border-right'}/>
                    <td colSpan={13} className={'p-0 no-left-border'}/>
                </tr>
                </tbody>

                <tbody
                    id={ElementIds.printPurchaseView.productsTable.footerContinue}
                    className={'table-footer-border'}
                >
                <tr className={'side-borders no-bottom-border no-top-border'}>
                    <td colSpan={36} className={'text-left font-weight-bold text-lg py-2-5 border-right'}>
                        {t('footer.continued')}
                    </td>
                    <td colSpan={24} className={'no-left-border'}/>
                </tr>
                </tbody>

                <tbody
                    id={ElementIds.printPurchaseView.productsTable.footer}
                    className={'table-footer-border'}
                >
                {
                    !!discount &&
                    <tr className={'side-borders no-bottom-border no-top-border'}>
                        <td colSpan={36} className={'border-right'}/>
                        <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border no-right-border'}>
                            {t('footer.discount')}
                        </td>
                        <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border'}>
                            {`-${formatMoney(discount).replace('$', '')}`}
                        </td>
                    </tr>
                }
                {
                    !!tax &&
                    <tr className={'side-borders no-bottom-border no-top-border'}>
                        <td colSpan={36} className={'border-right'}/>
                        <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border no-right-border'}>
                            {t('footer.tax')}
                        </td>
                        <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border'}>
                            {formatMoney(tax).replace('$', '')}
                        </td>
                    </tr>
                }
                <tr className={'side-borders no-bottom-border no-top-border'}>
                    <td colSpan={36} className={'border-right'}/>
                    <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border no-right-border'}>
                        {t('footer.netAmount')}
                    </td>
                    <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border'}>
                        {formatMoney(netAmount).replace('$', '')}
                    </td>
                </tr>
                <tr className={'side-borders no-bottom-border no-top-border'}>
                    <td colSpan={36} className={'border-right'}/>
                    <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border no-right-border'}>
                        {t('footer.freight')}
                    </td>
                    <td colSpan={12} className={'text-right font-weight-semi-bold text-sm no-left-border'}>
                        {formatMoney(freight).replace('$', '')}
                    </td>
                </tr>
                <tr className={'side-borders no-bottom-border no-top-border'}>
                    <td colSpan={36} className={'text-left font-weight-semi-bold text-sm border-right'}>
                        {purchaseDocument?.currency ?? ''}
                    </td>
                    <td colSpan={12} className={'text-right font-weight-bold text-lg no-left-border no-right-border'}>
                        {t('footer.totalDue')}
                    </td>
                    <td colSpan={12} className={'text-right font-weight-bold text-lg no-left-border'}>
                        {formatMoney(totalDue).replace('$', '')}
                    </td>
                </tr>
                <tr className={'side-borders no-bottom-border no-top-border'}>
                    <td colSpan={36} className={'py-1 border-right'}/>
                    <td colSpan={24} className={'py-1 no-left-border'}/>
                </tr>
                </tbody>
            </table>
        </div>
    );
};

export default PrintPurchaseTemplate;
